import {ref, Ref} from 'vue';
import ServiceBase from '@/services/ServiceBase';
import ApiService from '@/services/ApiService';
import {useAuthStore} from '@/stores/Auth';
import type {RawAxiosRequestHeaders, AxiosHeaders} from 'axios';

// Environment variables and storage keys
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC ?? '';
const STORAGE_KEY = 'push-subscription-id';
const STORAGE_KEY_ENDPOINT = 'push-subscription-endpoint';
const STORAGE_KEY_OPT_OUT = 'push-disabled-by-user';

export type PushEnableResult = 'enabled' | 'denied' | 'unsupported' | 'error';

export default class WebPushService extends ServiceBase {
    // Reactive state for Vue templates
    // iOS Safari (non-PWA) has no global Notification — guard to avoid ReferenceError at construction
    public readonly permission: Ref<NotificationPermission> = ref(
        'Notification' in window ? Notification.permission : 'default'
    );
    public readonly subscriptionId: Ref<string | null> = ref(localStorage.getItem(STORAGE_KEY));
    public readonly isBusy: Ref<boolean> = ref(false);

    // Singleton instance
    private static instance: WebPushService | null = null;

    private readonly api = ApiService.getInstance();

    // Prevent direct instantiation
    private constructor() {
        super();
    }

    public static getInstance(): WebPushService {
        if (!this.instance) {
            this.instance = new WebPushService();
        }
        return this.instance;
    }

    /**
     * Converts a Base64 URL string to a Uint8Array.
     * @param data
     * @private
     */
    private static decodeBase64Url(data: string): Uint8Array {
        const padding = '='.repeat((4 - (data.length % 4)) % 4);
        const base64 = data.replace(/-/g, '+').replace(/_/g, '/') + padding;
        return Uint8Array.from(atob(base64), char => char.charCodeAt(0));
    }

    /**
     * Checks if the browser supports service workers and PushManager.
     * @private
     */
    private static isPushSupported(): boolean {
        return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    }

    /**
     * Retrieves authentication headers for API requests.
     * @private
     */
    private async getAuthHeaders(): Promise<RawAxiosRequestHeaders | AxiosHeaders> {
        const authStore = useAuthStore();
        const token = await authStore.getAccessToken();
        return token ? {Authorization: `Bearer ${token}`} : {};
    }

    /**
     * Requests notification permission from the user.
     * @private
     */
    private async requestNotificationPermission(): Promise<NotificationPermission> {
        return Notification.requestPermission();
    }

    /**
     * Gets existing push subscription or creates a new one.
     * @private
     */
    private async getOrCreateSubscription(): Promise<PushSubscription> {
        const registration = await navigator.serviceWorker.ready;
        const existingSubscription = await registration.pushManager.getSubscription();
        if (existingSubscription) {
            return existingSubscription;
        }

        const applicationKey = WebPushService.decodeBase64Url(VAPID_PUBLIC_KEY);
        return registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: applicationKey,
        });
    }

    /**
     * Sends subscription details to backend and stores the returned ID.
     * @param subscription
     * @private
     */
    private async registerSubscription(subscription: PushSubscription): Promise<string> {
        // skip POST only if THIS exact browser subscription is already registered
        const storedEndpoint = localStorage.getItem(STORAGE_KEY_ENDPOINT);
        const storedId = localStorage.getItem(STORAGE_KEY);
        if (storedEndpoint === subscription.endpoint && storedId) {
            this.subscriptionId.value = storedId;
            return storedId;
        }

        const authStore = useAuthStore();
        const mandant = await authStore.getCurrentMandant();
        const url = `mandanten/${mandant.mandantId}/webpush/subscribe`;
        const response = await this.api.httpClient.post<string>(url, subscription, {
            headers: await this.getAuthHeaders(),
        });

        const returnedId = response.data;
        this.subscriptionId.value = returnedId;
        localStorage.setItem(STORAGE_KEY, returnedId);
        localStorage.setItem(STORAGE_KEY_ENDPOINT, subscription.endpoint);
        return returnedId;
    }

    /**
     * Tells the backend to remove the subscription with the given endpoint and
     * clears the locally cached id/endpoint. Mirror of {@link registerSubscription}.
     * @private
     */
    private async unregisterSubscription(endpointToDelete: string): Promise<void> {
        const authStore = useAuthStore();
        const mandant = await authStore.getCurrentMandant();
        if (mandant?.mandantId == null) {
            console.warn('unregisterSubscription: no mandant available, skipping backend call');
            return;
        }

        const url = `mandanten/${mandant.mandantId}/webpush/unsubscribe`;
        try {
            await this.api.httpClient.delete(url, {
                headers: await this.getAuthHeaders(),
                params: {endpoint: endpointToDelete},
            });
        } catch (err) {
            console.error('backend unsubscribe failed', err);
            throw err;
        }

        this.subscriptionId.value = null;
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_KEY_ENDPOINT);
    }

    /**
     * Enables push notifications: permission, subscription, and backend registration.
     */
    public async enablePushNotifications(): Promise<PushEnableResult> {
        if (!WebPushService.isPushSupported()) {
            return 'unsupported';
        }

        // explicit user action — clear any prior opt-out
        localStorage.removeItem(STORAGE_KEY_OPT_OUT);

        this.isBusy.value = true;
        try {
            const permission = await this.requestNotificationPermission();
            this.permission.value = permission;
            if (permission !== 'granted') {
                return 'denied';
            }

            const subscription = await this.getOrCreateSubscription();
            await this.registerSubscription(subscription);
            return 'enabled';
        } catch (err) {
            console.error('enablePushNotifications failed', err);
            return 'error';
        } finally {
            this.isBusy.value = false;
        }
    }

    /**
     * Background variant of {@link enablePushNotifications} for the auth flow.
     * - Respects the user's opt-out flag (set by {@link disablePushNotifications}).
     * - Never prompts for permission — only proceeds if permission is already granted.
     * - Silent: returns void, errors are logged but never thrown.
     */
    public async tryAutoEnable(): Promise<void> {
        if (!WebPushService.isPushSupported()) return;
        if (localStorage.getItem(STORAGE_KEY_OPT_OUT)) return;
        if (Notification.permission !== 'granted') return;

        this.isBusy.value = true;
        try {
            const subscription = await this.getOrCreateSubscription();
            await this.registerSubscription(subscription);
        } catch (err) {
            console.error('tryAutoEnable failed', err);
        } finally {
            this.isBusy.value = false;
        }
    }

    /**
     * Disables push notifications: removes the subscription from the backend,
     * unsubscribes from the browser's PushManager, and clears local state.
     */
    public async disablePushNotifications(): Promise<void> {
        if (!WebPushService.isPushSupported()) return;

        // explicit user action — remember opt-out so auto-enable won't override it
        localStorage.setItem(STORAGE_KEY_OPT_OUT, '1');

        this.isBusy.value = true;
        try {
            const registration = await navigator.serviceWorker.ready;
            const existingSubscription = await registration.pushManager.getSubscription();

            // figure out which endpoint to tell the backend about — the live
            // browser subscription if available, otherwise the one we cached
            // in localStorage from the last register call.
            const endpointToDelete =
                existingSubscription?.endpoint ?? localStorage.getItem(STORAGE_KEY_ENDPOINT);

            if (endpointToDelete) {
                await this.unregisterSubscription(endpointToDelete);
            } else {
                console.warn('disablePushNotifications: no endpoint found (neither browser nor localStorage)');
            }

            if (existingSubscription) {
                await existingSubscription.unsubscribe();
            }
        } finally {
            this.isBusy.value = false;
        }
    }

    /**
     * Sends a test notification for admin users.
     */
    public async sendTestNotification(): Promise<void> {
        console.log("subscriptionId", this.subscriptionId);
        const authStore = useAuthStore();
        const mandant = await authStore.getCurrentMandant();
        if (!this.subscriptionId.value) {
            return;
        }

        const triggerEndpoint = `mandanten/${mandant.mandantId}/webpush/trigger/${this.subscriptionId.value}`;
        await this.api.httpClient.post(triggerEndpoint, {}, {
            headers: await this.getAuthHeaders(),
        });
    }
}
